/**
 * Cerberus AuthProvider — top-level context provider.
 *
 * Lifecycle:
 *   1. On mount, calls `consumeReturnFromUrl()` to pick up tokens left in the
 *      hash fragment by the cerberus-auth-ui redirect.
 *   2. Loads the stored access token, verifies signature + claims via JWKS,
 *      and transitions to `authed` on success.
 *   3. Schedules a silent refresh at `(exp - REFRESH_LEAD_SECONDS)`.
 *   4. Listens to `storage` events to react to other-tab logout / refresh.
 *   5. When `requireAuth` (default true) and status is `unauthed`, redirects
 *      to the cerberus auth UI.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { AuthContext, type AuthContextValue, type AuthStatus } from './AuthContext'
import { REFRESH_LEAD_SECONDS } from './config'
import { consumeReturnFromUrl, redirectToLogin } from './redirect'
import {
  ACCESS_KEY,
  ACTIVE_ORG_KEY,
  clearTokens,
  getAccessToken,
  getActiveOrgId,
  getUserId,
} from './tokens'
import type {
  AccessClaims,
  CerberusUser,
  DomainProfileClaim,
  Membership,
} from './types'

/**
 * JWT verification + refresh are only needed when a stored token exists.
 * Dynamic-import them so the (much larger) crypto+jwks code path stays out
 * of the initial bundle on the cold-load /login path where there is no
 * token to verify yet.
 */
async function verifyAccessTokenLazy(
  token: string,
): Promise<AccessClaims> {
  const mod = await import('./jwt')
  return mod.verifyAccessToken(token)
}
async function refreshAccessTokenLazy(opts?: {
  redirectOnFailure?: boolean
  activeDomain?: string | null
}): Promise<void> {
  const mod = await import('./refresh')
  await mod.refreshAccessToken(opts)
}

interface AuthProviderProps {
  children: ReactNode
  /**
   * When true (default), an `unauthed` state redirects the user to the
   * cerberus auth UI. Set to false in storybook / public landing pages.
   */
  requireAuth?: boolean
  /**
   * When set, used as the fallback redirect URL. Defaults to the current
   * `window.location.href` so the auth UI can bounce the user back.
   */
  returnTo?: string
}

const DEV_AUTH_ENABLED =
  import.meta.env.DEV &&
  (import.meta.env.VITE_CERBERUS_DEV_AUTH === '1' ||
    import.meta.env.VITE_CONSOLE_DEV_AUTH === '1')

function claimsToUser(claims: AccessClaims, userId: string): CerberusUser {
  return {
    id: userId,
    email: typeof claims.email === 'string' ? claims.email : undefined,
    memberships: claims.memberships ?? [],
    grants: claims.grants ?? [],
    is_super_admin: Boolean(claims.is_super_admin),
  }
}

function pickInitialOrgId(
  memberships: Membership[],
  stored: string | null,
): string | null {
  if (memberships.length === 0) return null
  if (stored && memberships.some((m) => m.org_id === stored)) return stored
  return memberships[0]?.org_id ?? null
}

export function AuthProvider({
  children,
  requireAuth = true,
  returnTo,
}: AuthProviderProps) {
  if (DEV_AUTH_ENABLED) {
    return <DevAuthProvider>{children}</DevAuthProvider>
  }

  return (
    <RealAuthProvider requireAuth={requireAuth} returnTo={returnTo}>
      {children}
    </RealAuthProvider>
  )
}

function DevAuthProvider({ children }: { children: ReactNode }) {
  const devTenantId =
    import.meta.env.VITE_CONSOLE_DEV_TENANT_ID || 'org_dev_console'
  const [activeOrgId, setActiveOrgId] = useState<string | null>(devTenantId)

  const value = useMemo<AuthContextValue>(() => {
    const now = Math.floor(Date.now() / 1000)
    const claims: AccessClaims = {
      iss: 'dev://cerberus',
      sub: 'usr_dev_console',
      sid: 'sid_dev_console',
      iat: now,
      exp: now + 60 * 60 * 24,
      jti: 'jti_dev_console',
      typ: 'access',
      memberships: [{ org_id: devTenantId, role: 'owner' }],
      grants: [
        { org_id: devTenantId, domain: 'finance', tier: 'admin' },
        { org_id: devTenantId, domain: 'pheme', tier: 'admin' },
        { org_id: devTenantId, domain: 'console-admin', tier: 'admin' },
      ],
      is_super_admin: true,
      email: 'dev@francisquini.ai',
      active_domain: 'pheme',
      domain_profiles: [
        { domain: 'pheme', display_name: 'Pheme', locale: 'pt-BR' },
        { domain: 'finance', display_name: 'Cuidari', locale: 'pt-BR' },
      ],
    }
    const user = claimsToUser(claims, claims.sub)

    return {
      status: 'authed',
      user,
      claims,
      memberships: user.memberships,
      grants: user.grants,
      isSuperAdmin: true,
      activeOrgId,
      setActiveOrgId,
      hasGrant: () => true,
      logout: () => undefined,
      activeDomain: 'pheme',
      domainProfiles: claims.domain_profiles ?? [],
      switchDomain: async () => undefined,
    }
  }, [activeOrgId, devTenantId])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function RealAuthProvider({
  children,
  requireAuth = true,
  returnTo,
}: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [claims, setClaims] = useState<AccessClaims | null>(null)
  const [user, setUser] = useState<CerberusUser | null>(null)
  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(() =>
    getActiveOrgId(),
  )
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mounted = useRef(true)
  const loadRef = useRef<(consumeHash: boolean) => Promise<void>>(
    async () => {},
  )

  const scheduleRefresh = useCallback((nextClaims: AccessClaims) => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current)
      refreshTimer.current = null
    }
    const nowSec = Math.floor(Date.now() / 1000)
    const delaySec = Math.max(
      5,
      nextClaims.exp - nowSec - REFRESH_LEAD_SECONDS,
    )
    refreshTimer.current = setTimeout(() => {
      void (async () => {
        try {
          await refreshAccessTokenLazy()
          await loadRef.current(false)
        } catch {
          // refreshAccessToken already cleared + redirected on terminal failure
          if (mounted.current) setStatus('unauthed')
        }
      })()
    }, delaySec * 1000)
  }, [])

  const load = useCallback(
    async (consumeHash: boolean): Promise<void> => {
      if (consumeHash) consumeReturnFromUrl()
      const token = getAccessToken()
      const userId = getUserId()
      if (!token || !userId) {
        if (mounted.current) {
          setClaims(null)
          setUser(null)
          setStatus('unauthed')
        }
        return
      }
      try {
        const verified = await verifyAccessTokenLazy(token)
        if (!mounted.current) return
        setClaims(verified)
        const nextUser = claimsToUser(verified, userId)
        setUser(nextUser)
        setActiveOrgIdState((prev) =>
          pickInitialOrgId(nextUser.memberships, prev ?? getActiveOrgId()),
        )
        setStatus('authed')
        scheduleRefresh(verified)
      } catch {
        // Verification failed — try refresh once before giving up.
        try {
          await refreshAccessTokenLazy({ redirectOnFailure: false })
          const t2 = getAccessToken()
          if (!t2) throw new Error('No access token after refresh')
          const verified2 = await verifyAccessTokenLazy(t2)
          if (!mounted.current) return
          setClaims(verified2)
          const u2 = claimsToUser(verified2, getUserId() ?? userId)
          setUser(u2)
          setActiveOrgIdState((prev) =>
            pickInitialOrgId(u2.memberships, prev ?? getActiveOrgId()),
          )
          setStatus('authed')
          scheduleRefresh(verified2)
        } catch {
          clearTokens()
          if (mounted.current) {
            setClaims(null)
            setUser(null)
            setStatus('unauthed')
          }
        }
      }
    },
    [scheduleRefresh],
  )

  // Expose the latest `load` to the silent-refresh timer (declared earlier).
  loadRef.current = load

  // Initial mount.
  useEffect(() => {
    mounted.current = true
    void load(true)
    return () => {
      mounted.current = false
      if (refreshTimer.current) {
        clearTimeout(refreshTimer.current)
        refreshTimer.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Cross-tab sync.
  useEffect(() => {
    function onStorage(ev: StorageEvent) {
      if (ev.storageArea && ev.storageArea !== sessionStorage) return
      if (ev.key === null || ev.key === ACCESS_KEY) {
        void load(false)
      } else if (ev.key === ACTIVE_ORG_KEY) {
        setActiveOrgIdState(getActiveOrgId())
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [load])

  // Enforce redirect when unauthed.
  useEffect(() => {
    if (!requireAuth) return
    if (status !== 'unauthed') return
    redirectToLogin(returnTo)
  }, [requireAuth, status, returnTo])

  const setActiveOrgId = useCallback((orgId: string | null) => {
    setActiveOrgIdState(orgId)
  }, [])

  /**
   * Domain-grant probe for ad-hoc UI checks.
   *
   * Behaviour across B2B and B2C grants (cerberus multi-tenant spec §4.2 +
   * §7.3):
   *
   * - `targetOrg = orgId ?? activeOrgId`. If both are null/undefined — i.e.
   *   the user has no active org selected (typical B2C user with empty
   *   `memberships`) — the org-id check short-circuits via `!targetOrg` and
   *   the result is driven purely by `g.domain`. Personal grants
   *   (`{user_id, domain, ...}`, `org_id` absent) therefore match correctly.
   * - When `targetOrg` is non-null (the user has explicitly selected a B2B
   *   org), only grants whose `g.org_id === targetOrg` match. Personal
   *   grants (no `org_id`) intentionally fail this check: a user that has
   *   switched into the lens of org X should not see their unrelated
   *   personal-domain access bleed through.
   * - `is_super_admin: true` short-circuits to true, unchanged.
   *
   * Note that `g.user_id` is never compared against the active user — the
   * server only emits a personal grant entry for the JWT subject, so
   * presence-of-grant already implies the match.
   */
  const hasGrant = useCallback(
    (domain: string, orgId?: string): boolean => {
      if (!user) return false
      if (user.is_super_admin) return true
      const targetOrg = orgId ?? activeOrgId
      return user.grants.some(
        (g) =>
          g.domain === domain && (!targetOrg || g.org_id === targetOrg),
      )
    },
    [user, activeOrgId],
  )

  const logout = useCallback(() => {
    clearTokens()
    setClaims(null)
    setUser(null)
    setStatus('unauthed')
    if (requireAuth) redirectToLogin(returnTo)
  }, [requireAuth, returnTo])

  /**
   * T8 — switch active domain by re-running /auth/refresh with the desired
   * `active_domain`. On success the silent-refresh timer is reset against
   * the freshly-minted access token. Throws on failure so the caller can
   * surface UX (toast / inline error).
   */
  const switchDomain = useCallback(
    async (domain: string): Promise<void> => {
      if (typeof domain !== 'string' || domain.length === 0) {
        throw new Error('switchDomain: domain must be a non-empty string')
      }
      // refreshAccessTokenLazy already persists the new tokens via setTokens.
      // We then re-run `load(false)` to re-verify + update React state.
      await refreshAccessTokenLazy({
        redirectOnFailure: false,
        activeDomain: domain,
      })
      await loadRef.current(false)
    },
    [],
  )

  const value = useMemo<AuthContextValue>(() => {
    const memberships = user?.memberships ?? []
    const grants = user?.grants ?? []
    const isSuperAdmin = Boolean(user?.is_super_admin)
    const domainProfiles: DomainProfileClaim[] = Array.isArray(
      claims?.domain_profiles,
    )
      ? (claims!.domain_profiles as DomainProfileClaim[])
      : []
    // active_domain may be absent on pre-T8 tokens: fall back to the first
    // granted domain so the UI always has something to render. This is
    // strictly a UX hint; authorization remains grants-driven.
    const activeDomain =
      typeof claims?.active_domain === 'string' && claims.active_domain.length > 0
        ? claims.active_domain
        : grants[0]?.domain ?? null
    return {
      status,
      user,
      claims,
      memberships,
      grants,
      isSuperAdmin,
      activeOrgId,
      setActiveOrgId,
      hasGrant,
      logout,
      activeDomain,
      domainProfiles,
      switchDomain,
    }
  }, [
    status,
    user,
    claims,
    activeOrgId,
    setActiveOrgId,
    hasGrant,
    logout,
    switchDomain,
  ])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
